import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { fetchConfig } from '@theme/utilities';

const SOURCE = 'aov-cart-drawer';
const INNER_ID = 'AovDrawerInner';

/**
 * The theme defines `const Theme = {...}` in a classic script: a global
 * lexical binding, not a window property. Resolve it either way.
 */
function themeGlobal() {
  // eslint-disable-next-line no-undef
  return (typeof Theme !== 'undefined' ? Theme : window.Theme) || {};
}

function themeRoutes() {
  return themeGlobal().routes || {};
}

/**
 * AOV cart drawer. Extends the theme's DialogComponent (native <dialog>,
 * top layer, scroll lock, Esc, click-outside). Content is server-rendered
 * Liquid; after any cart mutation the inner container is re-rendered via the
 * Section Rendering API and morphed in place. The <dialog> element itself is
 * never morphed, which preserves its open state.
 *
 * @extends {DialogComponent}
 */
class AovCartDrawer extends DialogComponent {
  /** Offer ids already reported as impressions this open. */
  #seenOffers = new Set();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener('visitorConsentCollected', this.#onConsent);
    this.addEventListener('click', this.#onClick);
    this.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
    this.addEventListener('submit', this.#onSubmit);

    if (this.hasAttribute('hijack-links')) {
      document.addEventListener('click', this.#onDocumentClick, true);
    }

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#onEditorSelect);
      document.addEventListener('shopify:section:deselect', this.closeDialog);
    }

    // Catch carts dirtied before this element existed (PDP adds, old tabs).
    if (this.limitOne) setTimeout(() => this.#normalizeQuantities(), 1500);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener('visitorConsentCollected', this.#onConsent);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
    this.removeEventListener('submit', this.#onSubmit);
    document.removeEventListener('click', this.#onDocumentClick, true);

    if (window.Shopify?.designMode) {
      document.removeEventListener('shopify:section:select', this.#onEditorSelect);
      document.removeEventListener('shopify:section:deselect', this.closeDialog);
    }
  }

  get sectionId() {
    return this.dataset.sectionId || 'aov-cart-drawer';
  }

  get limitOne() {
    return this.hasAttribute('limit-one');
  }

  get inner() {
    return this.querySelector(`#${INNER_ID}`);
  }

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  /**
   * Public refresh, used by the UpCart shim for refresh-only signals.
   */
  refresh() {
    return this.#refresh();
  }

  /**
   * Refresh drawer content, then open. Public so the UpCart shim and promo
   * CTAs can call one method for "something changed, show the cart".
   */
  refreshAndOpen() {
    this.#refreshOrNormalize();
    this.showDialog();
  }

  /**
   * On EDMProd most add-to-cart buttons post straight to /cart/add.js and never
   * dispatch cart:update, so #onCartUpdate (which is what enforces limit-one on
   * a stock Horizon product form) never runs for them. Normalize here too, or a
   * second click on the same product leaves quantity 2 on a digital product.
   */
  async #refreshOrNormalize() {
    const normalized = this.limitOne ? await this.#normalizeQuantities() : false;
    if (!normalized) this.#refresh();
  }

  /**
   * Cart changed somewhere in the theme (product form, quick add, cart page).
   * Refresh our content; auto-open unless the change came from us or from the
   * cart page's own quantity edits.
   * @param {CustomEvent} event
   */
  #onCartUpdate = async (event) => {
    const detail = event.detail ?? {};
    const source = detail.data?.source ?? detail.sourceId;

    if (source === SOURCE) return;
    if (detail.data?.didError) return;

    const fromCartPage = detail.data?.source === 'cart-items-component';

    if (this.hasAttribute('auto-open') && !fromCartPage && !this.refs.dialog.open) {
      this.showDialog();
    }

    const normalized = await this.#normalizeQuantities();
    if (!normalized) this.#refresh();
  };

  /**
   * limit-one enforcement: any line with quantity above 1 drops to 1, and
   * extra lines of an already-present product (variant differences included)
   * are removed, keeping the newest. Runs on external cart changes so adds
   * from PDP buttons and the /cart page are corrected within a moment.
   * @returns {Promise<boolean>} true if the cart was changed (and re-rendered).
   */
  async #normalizeQuantities() {
    if (!this.limitOne) return false;

    try {
      const cart = await (await fetch(`${themeRoutes().cart_url || '/cart'}.js`)).json();
      const updates = {};
      const seenProducts = new Set();

      for (const item of cart.items) {
        // Gift cards intentionally support quantities and multiple values.
        // The rest of EDMProd's digital catalog remains one per product.
        if (item.gift_card) continue;

        if (seenProducts.has(item.product_id)) {
          updates[item.key] = 0;
          continue;
        }
        seenProducts.add(item.product_id);
        if (item.quantity > 1) updates[item.key] = 1;
      }

      if (Object.keys(updates).length === 0) return false;

      const response = await fetch(`${themeRoutes().cart_update_url || '/cart/update'}.js`, {
        ...fetchConfig('json', {
          headers: { 'X-AOV-Internal': '1' },
          body: JSON.stringify({ updates, sections: this.sectionId }),
        }),
      });
      const updated = await response.json();

      if (!response.ok || updated.status) throw new Error(updated.message || 'Cart error');

      await this.#reconcile(updated.sections?.[this.sectionId]);
      this.#dispatchCartUpdate(updated);
      return true;
    } catch (error) {
      console.error('[aov-cart-drawer] quantity normalize failed', error);
      return false;
    }
  }

  #onEditorSelect = (event) => {
    if (event.target instanceof Element && event.target.contains(this)) {
      this.showDialog();
    }
  };

  /**
   * Delegated clicks inside the drawer: close, remove line, empty-state add.
   * Delegation lives on the custom element so it survives content morphs.
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const closeTrigger = target.closest('[data-aov-close]');
    if (closeTrigger) {
      if (closeTrigger.tagName === 'BUTTON') event.preventDefault();
      this.closeDialog();
      return;
    }

    const removeTrigger = target.closest('[data-aov-remove]');
    if (removeTrigger) {
      event.preventDefault();
      this.#removeLine(removeTrigger.getAttribute('data-aov-remove'), removeTrigger);
      return;
    }

    const addTrigger = target.closest('[data-aov-add]');
    if (addTrigger) {
      event.preventDefault();
      const offerId = addTrigger.getAttribute('data-aov-offer-id');
      const replaceKeys = (addTrigger.getAttribute('data-aov-replace') || '').split(',').filter(Boolean);
      let source = 'empty-state';
      if (offerId) source = replaceKeys.length ? `upgrade:${offerId}` : `offer:${offerId}`;
      this.#addVariant(addTrigger.getAttribute('data-aov-add'), addTrigger, source, offerId, replaceKeys);
    }
  };

  #onConsent = () => { this.#seenOffers.clear(); this.#trackImpressions(); };

  #onDialogOpen = () => {
    this.#track('aov_drawer_open', this.#cartContext());
    this.#trackImpressions();
  };

  #onDialogClose = () => {
    this.#seenOffers.clear();
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    if (event.target instanceof HTMLFormElement && event.target.classList.contains('aov-drawer__checkout-form')) {
      this.#track('aov_checkout_click', this.#cartContext());
    }
  };

  /**
   * Capture-phase document listener: plain left-clicks on links to the cart
   * page open the drawer instead. Modifier clicks, new-tab links, and links
   * inside the drawer pass through untouched.
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest('a[href]');
    if (!link || this.contains(link)) return;
    if (link.target && link.target !== '_self') return;

    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch {
      return;
    }

    if (url.origin !== window.location.origin) return;

    const cartPath = themeRoutes().cart_url || '/cart';
    const pathname = url.pathname.replace(/\/$/, '') || '/';
    if (pathname !== cartPath) return;

    event.preventDefault();
    this.refreshAndOpen();
  };

  async #reconcile(html) {
    if (this.#morphInner(html)) return;
    // A successful mutation can return a null section; retry only the read.
    if (!await this.#refresh()) throw new Error('Cart changed but could not be displayed');
  }

  #showError(message = 'We could not confirm the latest cart. Review your cart before retrying or checking out.') {
    let box = this.querySelector('[data-aov-error]');
    if (!box) {
      box = document.createElement('p');
      box.dataset.aovError = '';
      box.setAttribute('role', 'alert');
      box.style.cssText = 'padding:16px;margin:8px;color:#fff;background:#7f1d1d';
      this.refs.dialog.prepend(box);
    }
    box.replaceChildren(document.createTextNode(message + ' '));
    const link = document.createElement('a');
    link.href = themeRoutes().cart_url || '/cart';
    link.textContent = 'Review cart';
    link.style.cssText = 'color:inherit;text-decoration:underline';
    box.append(link);
  }

  async #refresh() {
    this.setAttribute('aria-busy', 'true');

    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false);
      if (!this.#morphInner(html)) throw new Error('Cart section unavailable');
      return true;
    } catch (error) {
      console.error('[aov-cart-drawer] refresh failed', error);
      this.#showError();
      return false;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {string} html - Rendered section HTML from the Section Rendering
   *   API or a cart AJAX `sections` payload.
   */
  #morphInner(html) {
    if (!html) return false;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = parsed.getElementById(INNER_ID);
    const current = this.inner;

    if (!fresh || !current) return false;

    morph(current, fresh);
    this.querySelector('[data-aov-error]')?.remove();
    this.#restoreFocus();
    this.#trackImpressions();
    return true;
  }

  #restoreFocus() {
    if (!this.refs.dialog.open) return;
    if (this.contains(document.activeElement) && document.activeElement !== document.body) return;

    const fallback = this.querySelector('[data-aov-remove]') || this.querySelector('[data-aov-close]');
    if (fallback instanceof HTMLElement) fallback.focus();
  }

  /**
   * @param {string | null} key - Cart line item key.
   * @param {Element | null} trigger - The remove button, disabled in flight.
   */
  async #removeLine(key, trigger) {
    if (!key) return;

    trigger?.setAttribute('disabled', '');
    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${themeRoutes().cart_change_url || '/cart/change'}.js`, {
        ...fetchConfig('json', {
          headers: { 'X-AOV-Internal': '1' },
          body: JSON.stringify({ id: key, quantity: 0, sections: this.sectionId }),
        }),
      });
      const cart = await response.json();

      if (!response.ok || cart.status) throw new Error(cart.message || cart.description || 'Cart error');

      await this.#reconcile(cart.sections?.[this.sectionId]);
      this.#announce('Item removed');
      this.#dispatchCartUpdate(cart);
    } catch (error) {
      console.error('[aov-cart-drawer] remove failed', error);
      this.#showError();
      trigger?.removeAttribute('disabled');
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {string | null} variantId - Variant to add (offers, empty-state cards).
   * @param {Element | null} trigger - The add button, disabled in flight.
   * @param {string} [source] - Attribution written to the hidden _aov_source line property.
   * @param {string | null} [offerId] - Offer block id when the add came from an offer card.
   * @param {string[]} [replaceKeys] - Cart line keys to remove after the add (upgrade mode).
   */
  async #addVariant(variantId, trigger, source = 'drawer', offerId = null, replaceKeys = []) {
    if (!variantId) return;

    trigger?.setAttribute('disabled', '');
    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(themeRoutes().cart_add_url || '/cart/add.js', {
        ...fetchConfig('json', {
          headers: { 'X-AOV-Internal': '1' },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1, properties: { _aov_source: source } }],
            sections: this.sectionId,
          }),
        }),
      });
      if (!response.ok) throw new Error(`Cart add failed (${response.status})`);
      const result = await response.json();

      if (result.status) throw new Error(result.message || result.description || 'Cart error');

      let sectionsHtml = result.sections?.[this.sectionId];
      let upgradeSucceeded = replaceKeys.length === 0;
      let rollbackSucceeded = false;

      if (replaceKeys.length) {
        // Upgrade: add the destination first, then remove every covered line
        // in one update. If that swap fails, attempt to roll back the new line
        // and never announce or track a successful upgrade.
        try {
          const updates = {};
          for (const key of replaceKeys) updates[key] = 0;

          const swapResponse = await fetch(`${themeRoutes().cart_update_url || '/cart/update'}.js`, {
            ...fetchConfig('json', {
              headers: { 'X-AOV-Internal': '1' },
              body: JSON.stringify({ updates, sections: this.sectionId }),
            }),
          });
          if (!swapResponse.ok) throw new Error(`Upgrade swap failed (${swapResponse.status})`);
          const swapped = await swapResponse.json();

          if (swapped.status) throw new Error(swapped.message || swapped.description || 'Upgrade swap failed');

          upgradeSucceeded = true;
          sectionsHtml = swapped.sections?.[this.sectionId];
        } catch (error) {
          console.error('[aov-cart-drawer] upgrade swap failed', error);
          sectionsHtml = undefined; // Re-read after an uncertain swap or rollback.

          const addedKey = result.items?.[0]?.key || result.key;
          if (addedKey) {
            try {
              const rollbackResponse = await fetch(`${themeRoutes().cart_change_url || '/cart/change'}.js`, {
                ...fetchConfig('json', {
                  headers: { 'X-AOV-Internal': '1' },
                  body: JSON.stringify({ id: addedKey, quantity: 0, sections: this.sectionId }),
                }),
              });
              if (!rollbackResponse.ok) throw new Error(`Upgrade rollback failed (${rollbackResponse.status})`);
              const rolledBack = await rollbackResponse.json();
              if (rolledBack.status) {
                throw new Error(rolledBack.message || rolledBack.description || 'Upgrade rollback failed');
              }
              rollbackSucceeded = true;
              sectionsHtml = rolledBack.sections?.[this.sectionId];
            } catch (rollbackError) {
              console.error('[aov-cart-drawer] upgrade rollback failed', rollbackError);
            }
          }
        }
      }

      const cartResponse = await fetch(`${themeRoutes().cart_url || '/cart'}.js`);
      if (!cartResponse.ok) throw new Error(`Cart refresh failed (${cartResponse.status})`);
      const cart = await cartResponse.json();

      await this.#reconcile(sectionsHtml);
      this.#dispatchCartUpdate(cart);

      if (replaceKeys.length && !upgradeSucceeded) {
        this.#showError(
          rollbackSucceeded
            ? 'Upgrade was not completed. Please try again.'
            : 'Upgrade was not completed. Review your cart before checkout.'
        );
        if (offerId) {
          this.#track('aov_offer_error', {
            offer_id: offerId,
            variant_id: variantId,
            mode: 'upgrade',
            rollback_succeeded: rollbackSucceeded,
            cart_total: cart.total_price,
            item_count: cart.item_count,
          });
        }
        return;
      }

      this.#announce(replaceKeys.length ? 'Upgraded' : themeGlobal().translations?.added || 'Added to cart');

      if (offerId) {
        this.#track('aov_offer_add', {
          offer_id: offerId,
          variant_id: variantId,
          mode: replaceKeys.length ? 'upgrade' : 'add',
          cart_total: cart.total_price,
          item_count: cart.item_count,
        });
      }
    } catch (error) {
      console.error('[aov-cart-drawer] add failed', error);
      this.#showError();
      trigger?.removeAttribute('disabled');
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * Absolute item count with our source so cart-icon sets rather than adds,
   * and so our own cart:update listener ignores the echo.
   * @param {object} cart - Cart JSON from the AJAX API.
   */
  #dispatchCartUpdate(cart) {
    document.dispatchEvent(
      new CartUpdateEvent(cart, SOURCE, {
        itemCount: cart.item_count,
        source: SOURCE,
      })
    );
  }

  /** @param {string} message */
  #announce(message) {
    const live = this.querySelector('[data-aov-live]');
    if (live) live.textContent = message;
  }

  /**
   * Report each visible offer card once per drawer-open session.
   */
  #trackImpressions() {
    if (!this.refs.dialog.open || !this.#analyticsAllowed()) return;

    for (const card of this.querySelectorAll('div[data-aov-offer-id]')) {
      const id = card.getAttribute('data-aov-offer-id');
      if (!id || this.#seenOffers.has(id)) continue;
      this.#seenOffers.add(id);
      this.#track('aov_offer_impression', {
        ...this.#cartContext(),
        offer_id: id,
        product: card.getAttribute('data-aov-offer-product') || undefined,
        mode: card.getAttribute('data-aov-mode') || undefined,
      });
    }
  }

  /**
   * Cart context server-rendered onto the morphing inner container. This keeps
   * analytics current after add, remove, and upgrade operations without an
   * extra cart request for every event.
   */
  #cartContext() {
    return {
      cart_total: Number(this.inner?.dataset.cartTotal || 0),
      item_count: Number(this.inner?.dataset.itemCount || 0),
    };
  }

  /**
   * dataLayer push, gated by the section's analytics setting.
   * @param {string} name
   * @param {Record<string, unknown>} data
   */
  #analyticsAllowed() {
    return this.hasAttribute('data-analytics') && window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  }

  #track(name, data) {
    if (!this.#analyticsAllowed()) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...data });
  }
}

if (!customElements.get('aov-cart-drawer')) {
  customElements.define('aov-cart-drawer', AovCartDrawer);
}
